/*
 * Copyright 2026 North Carolina State University
 */

import { useEffect, useState } from 'react';
import axios from 'axios';

const BASE = '/api/ipm';

export const errorMessage = (err, fallback) => err?.response?.data?.error || fallback;

export const fileUrl = (id) => `${axios.defaults.baseURL || ''}${BASE}/files/${id}`;

export const ipmApi = {
  forms: (kind, includeArchived = false) => axios.get(`${BASE}/forms`, { params: { kind, include_archived: includeArchived } }).then(r => r.data),
  reorderForms: (ids) => axios.put(`${BASE}/forms/order`, { ids }).then(r => r.data),
  summary: () => axios.get(`${BASE}/summary`).then(r => r.data),
  createForm: (form) => axios.post(`${BASE}/forms`, form).then(r => r.data),
  saveForm: (id, form) => axios.put(`${BASE}/forms/${id}`, form).then(r => r.data),
  deleteForm: (id) => axios.delete(`${BASE}/forms/${id}`).then(r => r.data),
  createStarterForms: (kind) => axios.post(`${BASE}/forms/starter`, null, { params: { kind } }).then(r => r.data),

  entries: (params) => axios.get(`${BASE}/entries`, { params }).then(r => r.data),
  createEntry: (form, data) => axios.post(`${BASE}/entries`, { form, data }).then(r => r.data),
  updateEntry: (id, data) => axios.put(`${BASE}/entries/${id}`, { data }).then(r => r.data),
  deleteEntry: (id) => axios.delete(`${BASE}/entries/${id}`).then(r => r.data),

  uploadFiles: (files) => {
    const body = new FormData();
    [...files].forEach(f => body.append('files', f));
    return axios.post(`${BASE}/files`, body, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};

// Pickers for compartment / project / person / control fields. Loaded once per
// page load; invalidateLookup() after changing what one lists.
const lookupCache = {};
const lookupListeners = new Set();
export const invalidateLookup = (kind) => {
  delete lookupCache[kind];
  lookupListeners.forEach(fn => fn(kind));
};
export const loadLookup = (kind) => {
  if (!lookupCache[kind]) {
    lookupCache[kind] = axios.get(`${BASE}/${kind}`).then(r => r.data).catch((err) => {
      delete lookupCache[kind];
      throw err;
    });
  }
  return lookupCache[kind];
};

export const useLookup = (kind, enabled = true) => {
  const [items, setItems] = useState([]);
  const [stamp, setStamp] = useState(0);
  useEffect(() => {
    const onChange = (k) => { if (k === kind) setStamp(n => n + 1); };
    lookupListeners.add(onChange);
    return () => lookupListeners.delete(onChange);
  }, [kind]);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadLookup(kind).then(data => { if (alive) setItems(data); }).catch(() => {});
    return () => { alive = false; };
  }, [kind, enabled, stamp]);
  return items;
};
